import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useBlocker } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SqsEvents, trackingSourceList } from "@repo/analytics";
import { useIsMobile } from "@repo/utils";
import { SELF_LEARN_ROUTES } from "../../../../routes";
import { useFlashcardScreenController } from "./hooks/useFlashcardScreenController";
import {
  FlashcardCarousel,
  type FlashcardCarouselHandle,
} from "./components/FlashcardCarousel";
import { FlashcardCardBody } from "./components/FlashcardCardBody";
import { FlashcardShimmer } from "./components/FlashcardShimmer";
import { FlashcardsWebHeader } from "./components/FlashcardsWebHeader";
import { FlashcardProgressRow } from "./components/FlashcardProgressRow";
import { FlashcardOutlineSidebar } from "./components/FlashcardOutlineSidebar";
import { FlashcardLeaveModal } from "./components/FlashcardLeaveModal";
import { FlashcardImageLightbox } from "./components/FlashcardImageLightbox";
import type {
  FlashcardsLocationState,
  ReviseQuestionsLocationState,
} from "./types/flashcardsRoute";
import type { FlashCardUI } from "./models/FlashcardUI";
import styles from "./FlashcardsPage.module.scss";
import { defaultSelfLearnSession } from "../../../../lib/selfLearnSession";

const MemoCardBody = memo(FlashcardCardBody);

const FLASHCARDS_MWEB_MAX_WIDTH = 768;

let lastActSelfLearnFlashcardLocationKey: string | undefined;

export const FlashcardsPage = () => {
  const { t } = useTranslation("selflearn");
  const navigate = useNavigate();
  const location = useLocation();
  const gradeId = defaultSelfLearnSession?.gradeId ?? 0;
  const subtenantId = defaultSelfLearnSession?.subtenantId ?? 0;
  const examId = defaultSelfLearnSession?.examId ?? 0;

  const state = location.state as FlashcardsLocationState | null;
  const subjectId = state?.subjectId ?? 0;
  const chapterId = state?.chapterId ?? 0;
  const chapterName = state?.chapterName ?? "";
  const topicId = state?.topicId ?? 0;
  const topicName = state?.topicName ?? "";
  const flashcardTypeId = state?.flashcardTypeId ?? 1;
  const subjectName = state?.subjectName;

  const isNarrowLayout = useIsMobile(FLASHCARDS_MWEB_MAX_WIDTH);

  const [blockNav, setBlockNav] = useState(true);
  const allowExitRef = useRef(false);
  const blocker = useBlocker(() => blockNav && !allowExitRef.current);

  const [leaveOpen, setLeaveOpen] = useState(false);
  const [previewUri, setPreviewUri] = useState<string | null>(null);
  const carouselRef = useRef<FlashcardCarouselHandle>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [slideWidth, setSlideWidth] = useState(0);

  const {
    flashcards,
    currentIndex,
    currentCard,
    totalCards,
    setIndex,
    onBookmark,
    onLike,
    onDislike,
    isLoading,
    questionIds,
    onCardViewed,
    initialScrollIndex,
  } = useFlashcardScreenController({
    examId,
    gradeId,
    subtenantId,
    subjectId,
    chapterId,
    chapterName,
    topicId,
    topicName,
    flashcardTypeId,
  });

  useEffect(() => {
    if (lastActSelfLearnFlashcardLocationKey === location.key) return;
    lastActSelfLearnFlashcardLocationKey = location.key;

    const win =
      typeof globalThis !== "undefined" ? globalThis.window : undefined;
    const isMweb =
      typeof win?.innerWidth === "number" &&
      win.innerWidth <= FLASHCARDS_MWEB_MAX_WIDTH;

    SqsEvents.actSelfLearnFlashcard(
      {
        flashcardSubject: subjectName,
        flashcardChapter: chapterName,
        grade: gradeId,
        platform: isMweb ? "MWeb" : "Web",
      },
      {
        productOrigin: trackingSourceList.Il_website,
        productName: trackingSourceList.Il_website,
      },
    );
  }, [location.key]);

  const showContent = !isLoading && currentCard && totalCards > 0;

  const skipIndexChangeForScrollSyncRef = useRef<number | null>(null);

  const isQuestionsExists = questionIds.length ? 1 : 0;
  const totalWithQuestions = Math.max(totalCards, 0) + isQuestionsExists;

  const flashcardsCarouselKey = `sl-fc-${topicId}-${chapterId}`;

  const handleIndexChange = useCallback(
    (newIndex: number) => {
      if (skipIndexChangeForScrollSyncRef.current === newIndex) {
        skipIndexChangeForScrollSyncRef.current = null;
        return;
      }
      if (newIndex !== currentIndex && currentCard?.flashcardId != null) {
        onCardViewed(currentCard.flashcardId);
      }
      setIndex(newIndex);
    },
    [currentIndex, currentCard, onCardViewed, setIndex],
  );

  const goReviseQuestions = useCallback(() => {
    const payload: ReviseQuestionsLocationState = {
      questionIds,
      subjectId,
      topicId,
      topicName,
      chapterId,
      chapterName,
      ...(subjectName ? { subjectName } : {}),
    };
    navigate(`../${SELF_LEARN_ROUTES.REVISE_QUESTIONS}`, {
      relative: "path",
      replace: true,
      state: payload,
    });
  }, [
    navigate,
    questionIds,
    subjectId,
    topicId,
    topicName,
    chapterId,
    chapterName,
    subjectName,
  ]);

  const onReachedEnd = useCallback(() => {
    if (currentCard?.flashcardId != null) {
      onCardViewed(currentCard.flashcardId);
    }
    allowExitRef.current = true;
    setBlockNav(false);
    if (questionIds.length !== 0) {
      goReviseQuestions();
    } else {
      navigate(-1);
    }
  }, [
    currentCard,
    onCardViewed,
    questionIds.length,
    goReviseQuestions,
    navigate,
  ]);

  const goToNextByArrow = useCallback(() => {
    if (currentIndex >= totalCards - 1) {
      onReachedEnd();
      return;
    }
    if (currentCard?.flashcardId != null) {
      onCardViewed(currentCard.flashcardId);
    }
    const next = currentIndex + 1;
    skipIndexChangeForScrollSyncRef.current = next;
    setIndex(next);
    queueMicrotask(() => {
      carouselRef.current?.scrollToIndex(next);
    });
  }, [
    currentIndex,
    currentCard,
    onCardViewed,
    setIndex,
    totalCards,
    onReachedEnd,
  ]);

  const goToPreviousByArrow = useCallback(() => {
    if (currentIndex <= 0) return;
    if (currentCard?.flashcardId != null) {
      onCardViewed(currentCard.flashcardId);
    }
    const prev = currentIndex - 1;
    skipIndexChangeForScrollSyncRef.current = prev;
    setIndex(prev);
    queueMicrotask(() => {
      carouselRef.current?.scrollToIndex(prev);
    });
  }, [currentIndex, currentCard, onCardViewed, setIndex]);

  const openLeaveFlow = useCallback(() => {
    if (currentCard?.flashcardId != null) {
      onCardViewed(currentCard.flashcardId);
    }
    setLeaveOpen(true);
  }, [currentCard, onCardViewed]);

  useEffect(() => {
    if (blocker.state === "blocked") {
      setLeaveOpen(true);
    }
  }, [blocker.state]);

  const handleResumePractice = useCallback(() => {
    setLeaveOpen(false);
    if (blocker.state === "blocked") {
      allowExitRef.current = false;
      setBlockNav(true);
      blocker.reset();
    }
  }, [blocker]);

  const handleQuitPractice = useCallback(() => {
    setLeaveOpen(false);
    allowExitRef.current = true;
    setBlockNav(false);
    if (blocker.state === "blocked") {
      blocker.proceed();
    } else {
      navigate(-1);
    }
  }, [blocker, navigate]);

  const onOutlineSelectCard = useCallback(
    (index: number) => {
      if (index === currentIndex) return;
      if (currentCard?.flashcardId != null) {
        onCardViewed(currentCard.flashcardId);
      }
      skipIndexChangeForScrollSyncRef.current = index;
      setIndex(index);
      queueMicrotask(() => {
        carouselRef.current?.scrollToIndex(index);
      });
    },
    [currentIndex, currentCard, onCardViewed, setIndex],
  );

  const onOutlineSelectRevision = useCallback(() => {
    onReachedEnd();
  }, [onReachedEnd]);

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;

    const readWidth = () => {
      const cw = el.clientWidth;
      const rw = Math.round(el.getBoundingClientRect().width);
      return Math.max(cw, rw, 0);
    };

    const applyWidth = () => {
      setSlideWidth(readWidth());
    };

    applyWidth();

    const ro = new ResizeObserver(() => {
      applyWidth();
    });
    ro.observe(el);

    let raf0 = 0;
    let raf1 = 0;
    const scheduleDeferredMeasure = () => {
      cancelAnimationFrame(raf0);
      cancelAnimationFrame(raf1);
      raf0 = requestAnimationFrame(() => {
        raf1 = requestAnimationFrame(() => {
          const w = readWidth();
          if (w > 0) {
            setSlideWidth(w);
          }
        });
      });
    };
    scheduleDeferredMeasure();

    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf0);
      cancelAnimationFrame(raf1);
    };
  }, [isNarrowLayout, totalCards, showContent]);

  /**
   * MWeb: session `currentIndex` (header/progress) can be correct while the horizontal scroller
   * is still at scrollLeft=0 (layout + browser scroll behavior run after the hook's one-shot).
   * Re-apply the imperative scroll after commit + a couple of frames so the viewport matches state.
   */
  useLayoutEffect(() => {
    if (!isNarrowLayout || !showContent || totalCards === 0) {
      return;
    }
    if (slideWidth <= 0) {
      return;
    }

    const run = () => {
      carouselRef.current?.scrollToIndex(currentIndex);
    };

    run();
    const r0 = requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
    return () => cancelAnimationFrame(r0);
  }, [
    isNarrowLayout,
    showContent,
    currentIndex,
    totalCards,
    slideWidth,
    flashcardsCarouselKey,
  ]);

  const renderCard = useCallback(
    (item: FlashCardUI, index: number) => (
      <MemoCardBody
        card={item}
        onBookmark={() => onBookmark(item.flashcardId)}
        onNext={goToNextByArrow}
        onPrevious={goToPreviousByArrow}
        currentIndex={index}
        totalCards={totalCards}
        onLike={() => onLike(item.flashcardId)}
        onDislike={() => onDislike(item.flashcardId)}
        questionIds={questionIds}
        onImagePreviewOpen={(uri) => setPreviewUri(uri)}
        isNarrowLayout={isNarrowLayout}
      />
    ),
    [
      onBookmark,
      onLike,
      onDislike,
      questionIds,
      totalCards,
      goToNextByArrow,
      goToPreviousByArrow,
    ],
  );

  return (
    <div
      className={`${styles.page} ${isNarrowLayout ? styles.pageNarrow : ""}`}
    >
      <div
        className={`${styles.inner} ${isNarrowLayout ? styles.innerNarrow : ""}`}
      >
        <div
          className={`${styles.headerBlock} ${!isNarrowLayout ? styles.headerBlockWeb : ""}`}
        >
          <FlashcardsWebHeader
            onClose={openLeaveFlow}
            topicName={topicName || undefined}
            topicAlignStart={isNarrowLayout}
          />
          <div className={styles.progressWrap}>
            <FlashcardProgressRow
              currentIndex={currentIndex}
              totalWithQuestions={totalWithQuestions}
            />
          </div>
        </div>

        {showContent ? (
          isNarrowLayout ? (
            <div className={styles.mainNarrow}>
              <div className={`${styles.cardShell} ${styles.cardShellNarrow}`}>
                <div ref={measureRef} className={styles.carouselMeasure}>
                  <FlashcardCarousel
                    key={flashcardsCarouselKey}
                    ref={carouselRef}
                    flashcards={flashcards}
                    slideWidth={slideWidth}
                    currentIndex={currentIndex}
                    totalCards={totalCards}
                    allowHorizontalUserScroll
                    initialScrollIndex={initialScrollIndex}
                    onIndexChange={handleIndexChange}
                    onReachedEnd={onReachedEnd}
                    renderItem={renderCard}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.mainWeb}>
              <div className={`${styles.cardColumn} ${styles.cardColumnWeb}`}>
                <div className={styles.cardShell}>
                  <div ref={measureRef} className={styles.carouselMeasure}>
                    <FlashcardCarousel
                      key={flashcardsCarouselKey}
                      ref={carouselRef}
                      flashcards={flashcards}
                      slideWidth={slideWidth}
                      currentIndex={currentIndex}
                      totalCards={totalCards}
                      allowHorizontalUserScroll={false}
                      initialScrollIndex={initialScrollIndex}
                      onIndexChange={handleIndexChange}
                      onReachedEnd={onReachedEnd}
                      renderItem={renderCard}
                    />
                  </div>
                </div>
              </div>
              <aside className={styles.sidebar}>
                <FlashcardOutlineSidebar
                  flashcards={flashcards}
                  currentIndex={currentIndex}
                  hasRevisionQuestions={questionIds.length > 0}
                  onSelectCard={onOutlineSelectCard}
                  onSelectRevision={onOutlineSelectRevision}
                  subjectName={subjectName ?? ""}
                />
              </aside>
            </div>
          )
        ) : (
          <div className={styles.cardShell}>
            <FlashcardShimmer />
          </div>
        )}
      </div>

      <FlashcardLeaveModal
        isOpen={leaveOpen}
        onResume={handleResumePractice}
        onQuit={handleQuitPractice}
        title={t("leaveModal.title")}
        leftButtonText={
          isNarrowLayout
            ? t("leaveModal.quitRevision")
            : t("leaveModal.yesLeave")
        }
      />

      <FlashcardImageLightbox
        isOpen={previewUri != null}
        imageUri={previewUri ?? ""}
        onClose={() => setPreviewUri(null)}
      />
    </div>
  );
};
